/**
 * Media URL Helpers for Backend Services
 *
 * Provides unified URL generation for media assets served from GCS.
 * All assets are served from a single GCS bucket - single source of truth.
 *
 * Path structure in GCS:
 * - /static/videos/           - Pre-generated videos (lobby, NPCs)
 * - /static/staff-avatars/    - NPC avatars and portraits
 * - /static/images/           - UI images, game assets
 * - /static/modules/{slug}/   - Module hero images/videos
 * - /dynamic/characters/      - Generated character portraits
 * - /dynamic/backgrounds/     - Generated scene backgrounds
 * - /dynamic/narrator/        - Generated narrator images
 */

// GCS bucket URL (required for all environments)
const MEDIA_BUCKET_URL =
  process.env.VITE_MEDIA_BUCKET_URL ||
  process.env.GCS_MEDIA_BUCKET_URL ||
  'https://media.polymyth.quest';

/**
 * Get the base media bucket URL
 */
export function getMediaBucketUrl(): string {
  return MEDIA_BUCKET_URL;
}

/**
 * Get URL for a static asset
 *
 * @param path - Path relative to static directory (e.g., "videos/intro.mp4")
 * @returns Full GCS URL for the asset
 */
export function getStaticAssetUrl(path: string): string {
  return `${MEDIA_BUCKET_URL}/static/${path}`;
}

/**
 * Get URL for a video file
 *
 * @param filename - Video filename (e.g., "intro.mp4")
 * @returns Full GCS URL for the video
 */
export function getVideoUrl(filename: string): string {
  return getStaticAssetUrl(`videos/${filename}`);
}

/**
 * Get URL for a staff avatar
 *
 * @param filename - Avatar filename (e.g., "tavian-avatar.webp")
 * @returns Full GCS URL for the avatar
 */
export function getStaffAvatarUrl(filename: string): string {
  return getStaticAssetUrl(`staff-avatars/${filename}`);
}

/**
 * Get URL for a module hero image from a heroImage path
 *
 * Module registry stores paths like "data/modules/wolves_of_welton/hero_gen.webp"
 * This converts them to GCS URLs: "{bucket}/static/modules/wolves_of_welton/hero_gen.webp"
 *
 * @param heroImagePath - Path from module registry (e.g., "data/modules/wolves_of_welton/hero_gen.webp")
 * @returns Full GCS URL for the hero image
 */
export function getModuleHeroImageUrl(heroImagePath: string): string {
  // Strip the "data/" prefix if present and convert to static URL
  const normalizedPath = heroImagePath.replace(/^data\//, '');
  return `${MEDIA_BUCKET_URL}/static/${normalizedPath}`;
}

/**
 * Get URL for a module media file directly by module ID
 *
 * @param moduleId - Module ID/slug (e.g., "wolves_of_welton")
 * @param filename - Media filename (e.g., "hero_gen.webp")
 * @returns Full GCS URL for the module media
 */
export function getModuleMediaUrl(moduleId: string, filename: string): string {
  return getStaticAssetUrl(`modules/${moduleId}/${filename}`);
}

/**
 * Get URL for a dynamic/generated image
 *
 * @param path - Path relative to dynamic directory (e.g., "characters/char123/portrait.webp")
 * @returns Full GCS URL for the dynamic image
 */
export function getDynamicAssetUrl(path: string): string {
  return `${MEDIA_BUCKET_URL}/dynamic/${path}`;
}

/**
 * Get URL for a generated narrator image
 *
 * @param moduleSlug - Module slug (e.g., "wolves_of_welton")
 * @param type - Image type ("avatar" or "portrait")
 * @returns Full GCS URL for the narrator image
 */
export function getNarratorImageUrl(moduleSlug: string, type: 'avatar' | 'portrait'): string {
  return getDynamicAssetUrl(`narrator/${moduleSlug}/${type}.webp`);
}

/**
 * Get URL for a game UI image
 *
 * @param filename - Image filename (e.g., "narrator-avatar.webp")
 * @returns Full GCS URL for the image
 */
export function getGameImageUrl(filename: string): string {
  return getStaticAssetUrl(`images/game/${filename}`);
}
